import React, { useState, useEffect, useRef, useMemo } from "react";
import { Form, Modal } from "react-bootstrap";
import Select from "react-select";
import { Toaster, toast } from "react-hot-toast";

import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import Pagination from "../components/Pagination";
import {
  listSuppliers,
  postKarigarLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";
import { extractListRowsFromResponse, parseApiPagination } from "../utils/parseApiPagination";
import { generateKarigarLedgerPdfBlob } from "../utils/generateKarigarLedgerPdf";

const processKarigarLedgerData = (entries, { entireReport, fromDate, toDate }) => {
  const sortedEntries = [...entries].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    if (dateA.getTime() === dateB.getTime()) {
      return 0;
    }
    return dateA - dateB;
  });

  let openingGoldBalance = 0;
  let openingSilverBalance = 0;
  let openingAmountBalance = 0;
  let transactionsInPeriod = sortedEntries;

  if (!entireReport && fromDate) {
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);

    const openingEntries = [];
    const periodEntries = [];

    sortedEntries.forEach((entry) => {
      const entryDate = new Date(entry.date);
      entryDate.setHours(0, 0, 0, 0);

      if (entryDate < from) {
        openingEntries.push(entry);
      } else if (!toDate || entryDate <= new Date(toDate)) {
        periodEntries.push(entry);
      }
    });

    openingEntries.forEach((entry) => {
      openingGoldBalance +=
        (parseFloat(entry.gold_aavak) || 0) -
        (parseFloat(entry.gold_jaavak) || 0);
      openingSilverBalance +=
        (parseFloat(entry.silver_aavak) || 0) -
        (parseFloat(entry.silver_jaavak) || 0);
      openingAmountBalance +=
        (parseFloat(entry.aavak_amt) || 0) -
        (parseFloat(entry.jaavak_amt) || 0);
    });

    transactionsInPeriod = periodEntries;
  } else {
    openingGoldBalance = 0;
    openingSilverBalance = 0;
    openingAmountBalance = 0;
  }

  let goldBalance = openingGoldBalance;
  let silverBalance = openingSilverBalance;
  let amountBalance = openingAmountBalance;

  const ledgerData = transactionsInPeriod.map((entry) => {
    const gold_credit = parseFloat(entry.gold_aavak) || 0;
    const gold_debit = parseFloat(entry.gold_jaavak) || 0;
    goldBalance += gold_credit - gold_debit;

    const silver_credit = parseFloat(entry.silver_aavak) || 0;
    const silver_debit = parseFloat(entry.silver_jaavak) || 0;
    silverBalance += silver_credit - silver_debit;

    const amount_credit = parseFloat(entry.aavak_amt) || 0;
    const amount_debit = parseFloat(entry.jaavak_amt) || 0;
    amountBalance += amount_credit - amount_debit;

    return {
      date: entry.date || "-",
      gwt: "",
      lwt: "",
      credit: gold_credit ? gold_credit.toFixed(3) : "",
      debit: gold_debit ? gold_debit.toFixed(3) : "",
      balance: goldBalance.toFixed(3),
      silver_gwt: "",
      silver_lwt: "",
      silver_credit: silver_credit ? silver_credit.toFixed(3) : "",
      silver_debit: silver_debit ? silver_debit.toFixed(3) : "",
      silver_balance: silverBalance.toFixed(3),
      amount_credit: amount_credit ? amount_credit.toFixed(2) : "",
      amount_debit: amount_debit ? amount_debit.toFixed(2) : "",
      amount_balance: amountBalance.toFixed(2),
      type: entry.type || "-",
      pay_type: entry.pay_type,
    };
  });

  return ledgerData;
};

function KarigarLedger() {
  const { buttonClass } = useButtonTableStyle();
  const [selectedKarigar, setSelectedKarigar] = useState(null);
  const [karigarOptions, setKarigarOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pdfUrlRef = useRef(null);
  const [pdfData, setPdfData] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const initialFetchRef = useRef(false);
  const ignoreNextPageFetchRef = useRef(false);
  const pageCacheRef = useRef(new Map());
  const karigarSearchRequestRef = useRef(0);

  const fetchKarigars = async (prefix = "") => {
    const trimmed = String(prefix || "").trim();
    const requestId = ++karigarSearchRequestRef.current;

    if (!trimmed || trimmed.length < 2) {
      setKarigarOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await listSuppliers({ search: trimmed, status: 1 });
      if (requestId !== karigarSearchRequestRef.current) return;

      let list = [];
      if (isErpApiSuccess(response)) {
        list = extractListRowsFromResponse(response, ["suppliers"]);
      }

      const options = list
        .filter((karigar) => {
          const status = String(karigar?.status ?? "").trim().toLowerCase();
          if (!status) return true;
          return ["1", "active", "enabled", "true"].includes(status);
        })
        .map((karigar) => {
          const displayName =
            karigar.company_name ||
            karigar.companyName ||
            karigar.name ||
            karigar.fullName ||
            karigar.supplier_name ||
            karigar.short_code ||
            (karigar.id != null ? `Supplier #${karigar.id}` : "");
          const contactNumber =
            karigar.contact_number ||
            karigar.contactNumber ||
            karigar.mobile ||
            karigar.phone ||
            "";

          return {
            value: karigar.id,
            label: contactNumber
              ? `${displayName} (${contactNumber})`
              : displayName,
            companyName: karigar.company_name || karigar.companyName || "",
            name: karigar.name || displayName,
            city: karigar.city || "",
          };
        })
        .filter((option) => option.label);

      setKarigarOptions(options);
    } catch (error) {
      if (requestId !== karigarSearchRequestRef.current) return;
      setKarigarOptions([]);
    } finally {
      if (requestId === karigarSearchRequestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;
  }, []);

  // Clean up blob url on unmount
  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, []);

  // Listen for API URL changes and clear report
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in KarigarLedger...', event.detail);
      setLedgerRows([]);
      setPdfData(null);
      setShowPDFModal(false);
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
      setPdfBlobUrl(null);
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, []);

  const handleSubmit = async (
    e,
    { page = 1, limit = itemsPerPage, bypassCache = false } = {},
  ) => {
    if (e?.preventDefault) e.preventDefault();
    const refresh = Boolean(e) || bypassCache;
    const cacheKey = entireReport
      ? `${selectedKarigar?.value || ""}:${page}:${limit}`
      : "";
    if (refresh) pageCacheRef.current.clear();
    if (cacheKey && !refresh) {
      const cached = pageCacheRef.current.get(cacheKey);
      if (cached) {
        setLedgerRows(cached.ledgerRows);
        setPdfData(cached.pdfData);
        setCurrentPage(cached.currentPage);
        setTotalCount(cached.totalCount);
        setHasMorePages(cached.hasMorePages);
        setLoading(false);
        return;
      }
    }
    setLoading(true);

    const apiPayload = {
      kid: selectedKarigar.value,
    };

    if (entireReport) {
      apiPayload.type = "entire";
      apiPayload.page = page;
      apiPayload.limit = limit;
    } else {
      if (fromDate) apiPayload.from_date = fromDate;
      if (toDate) apiPayload.to_date = toDate;
    }

    try {
      const payload = await postKarigarLedger(apiPayload);

      if (isErpApiSuccess(payload) || payload?.message === "radha") {
        const entries = payload.data?.entries || [];

        if (!Array.isArray(entries) || entries.length === 0) {
          toast.error("No ledger entries found for this Karigar / date range.");
          return;
        }

        const finalLedgerData = processKarigarLedgerData(entries, {
          entireReport,
          fromDate,
          toDate,
        });

        // Save for table view
        setLedgerRows(finalLedgerData);

        const paginationMeta = entireReport
          ? parseApiPagination(payload, finalLedgerData.length)
          : null;

        if (entireReport && paginationMeta) {
          setCurrentPage(paginationMeta.page);
          setTotalCount(paginationMeta.total);
          setHasMorePages(paginationMeta.hasMore);
          if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
        } else {
          setCurrentPage(1);
          setTotalCount(finalLedgerData.length);
          setHasMorePages(false);
        }

        // Also prepare data for PDF
        const nextPdf = {
          ledgerData: finalLedgerData,
          karigarInfo: selectedKarigar,
          fromDate,
          toDate,
          entireReport,
        };
        setPdfData(nextPdf);
        if (cacheKey && paginationMeta) {
          pageCacheRef.current.set(cacheKey, {
            ledgerRows: finalLedgerData,
            pdfData: nextPdf,
            currentPage: paginationMeta.page,
            totalCount: paginationMeta.total,
            hasMorePages: paginationMeta.hasMore,
          });
        }
        toast.success("Ledger generated successfully!");
      } else {
        toast.error("Failed to fetch ledger data. Please try again.");
        console.error("Failed to fetch ledger data:", payload);
      }
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while fetching the ledger data."
        )
      );
      console.error("Error fetching karigar ledger:", error);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedKarigar?.value) {
      toast.error("Please select a party/karigar first.");
      return;
    }
    if (!ledgerRows.length && totalCount === 0) {
      toast.error("Please generate the ledger first.");
      return;
    }
    if (printLoading) return;

    setPrintLoading(true);

    const printPayload = {
      kid: selectedKarigar.value,
    };

    if (entireReport) {
      printPayload.type = "entire";
      printPayload.page = 0;
      printPayload.limit = 0;
    } else {
      if (fromDate) printPayload.from_date = fromDate;
      if (toDate) printPayload.to_date = toDate;
    }

    try {
      const payload = await postKarigarLedger(printPayload);

      if (isErpApiSuccess(payload) || payload?.message === "radha") {
        const entries = payload.data?.entries || [];

        if (!Array.isArray(entries) || entries.length === 0) {
          toast.error("No ledger entries found to print.");
          return;
        }

        const processedLedgerData = processKarigarLedgerData(entries, {
          entireReport,
          fromDate,
          toDate,
        });

        const karigarInfo = {
          name: selectedKarigar?.name || "",
          companyName: selectedKarigar?.companyName || "",
          city: selectedKarigar?.city || "",
          label: selectedKarigar?.label || "",
        };

        if (pdfUrlRef.current) {
          URL.revokeObjectURL(pdfUrlRef.current);
          pdfUrlRef.current = null;
        }

        const blob = await generateKarigarLedgerPdfBlob({
          ledgerData: processedLedgerData,
          karigarInfo,
          fromDate,
          toDate,
          entireReport,
        });

        const blobUrl = URL.createObjectURL(blob);
        pdfUrlRef.current = blobUrl;
        setPdfBlobUrl(blobUrl);
        setShowPDFModal(true);
      } else {
        toast.error("Failed to fetch ledger data for printing. Please try again.");
      }
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while generating the print data."
        )
      );
      console.error("Error generating karigar ledger for print:", error);
    } finally {
      setPrintLoading(false);
    }
  };

  // Totals for table view
  const totals = useMemo(() => {
    let totCreditGold = 0;
    let totCreditSilver = 0;
    let totCreditAmount = 0;
    let totDebitGold = 0;
    let totDebitSilver = 0;
    let totDebitAmount = 0;

    ledgerRows.forEach((row) => {
      if (!row.isOpening) {
        totCreditGold += parseFloat(row.credit || 0) || 0;
        totCreditSilver += parseFloat(row.silver_credit || 0) || 0;
        totCreditAmount += parseFloat(row.amount_credit || 0) || 0;
        totDebitGold += parseFloat(row.debit || 0) || 0;
        totDebitSilver += parseFloat(row.silver_debit || 0) || 0;
        totDebitAmount += parseFloat(row.amount_debit || 0) || 0;
      }
    });

    const lastRow = ledgerRows.length > 0 ? ledgerRows[ledgerRows.length - 1] : null;
    const closingGoldBalance = lastRow ? parseFloat(lastRow.balance || 0) || 0 : 0;
    const closingSilverBalance = lastRow ? parseFloat(lastRow.silver_balance || 0) || 0 : 0;
    const closingAmountBalance = lastRow ? parseFloat(lastRow.amount_balance || 0) || 0 : 0;

    return {
      creditGold: totCreditGold,
      creditSilver: totCreditSilver,
      creditAmount: totCreditAmount,
      debitGold: totDebitGold,
      debitSilver: totDebitSilver,
      debitAmount: totDebitAmount,
      closingGold: closingGoldBalance,
      closingSilver: closingSilverBalance,
      closingAmount: closingAmountBalance,
    };
  }, [ledgerRows]);

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card">
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Supplier/Karigar Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* Karigar Dropdown */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">Select Party</label>
                  <Select
                    options={karigarOptions}
                    value={selectedKarigar}
                    onChange={setSelectedKarigar}
                    onInputChange={(val, { action }) => {
                      if (action === "input-change") {
                        fetchKarigars(val);
                      }
                      return val;
                    }}
                    filterOption={() => true}
                    openMenuOnFocus
                    openMenuOnClick
                    noOptionsMessage={() =>
                      loading ? "Searching..." : "No matching parties"
                    }
                    placeholder={
                      loading ? "Loading..." : "Search and select Party..."
                    }
                    isClearable
                    isSearchable
                    isLoading={loading}
                  />
                </div>
                {/* From Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={!selectedKarigar}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Ledger -{" "}
                    {selectedKarigar?.label ? selectedKarigar.label : ""}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                    disabled={printLoading || loading}
                  >
                    {printLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Fetching records...
                      </>
                    ) : (
                      "Print"
                    )}
                  </button>
                </div>
                <div className="table-responsive mb-4 border rounded-1">
                  <table className="table text-nowrap mb-0 align-middle">
                    <thead className="text-dark fs-4">
                      <tr>
                        <th colSpan="2" className="text-center"></th>
                        <th colSpan="3" className="text-center text-success">
                          Credited
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                          style={{ color: "#dc3545" }}
                        >
                          Debited
                        </th>
                        <th colSpan="3" className="text-center">
                          Balance
                        </th>
                      </tr>
                      <tr>
                        <th className="text-nowrap">Date</th>
                        <th className="text-nowrap">Tag No</th>
                        <th className="text-end text-nowrap text-success">
                          Gold
                        </th>
                        <th className="text-end text-nowrap text-success">
                          Silver
                        </th>
                        <th className="text-end text-nowrap text-success">
                          Amount
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Gold
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Silver
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Amount
                        </th>
                        <th className="text-end text-nowrap">Gold</th>
                        <th className="text-end text-nowrap">Silver</th>
                        <th className="text-end text-nowrap">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((row, idx) => {
                        const cellStyle = row.isOpening
                          ? { backgroundColor: "#E3F2FD", fontWeight: 500 }
                          : undefined;
                        // Extract only date part (YYYY-MM-DD) from date string
                        const dateValue = row.isOpening
                          ? "-"
                          : row.date
                            ? row.date.split(" ")[0]
                            : "-";
                        return (
                          <tr key={idx}>
                            <td style={cellStyle}>{dateValue}</td>
                            <td style={cellStyle}>{row.type || "-"}</td>
                            <td
                              className={`text-end ${row.credit ? "text-success" : ""
                                }`}
                              style={cellStyle}
                            >
                              {row.credit || "-"}
                            </td>
                            <td
                              className={`text-end ${row.silver_credit ? "text-success" : ""
                                }`}
                              style={cellStyle}
                            >
                              {row.silver_credit || "-"}
                            </td>
                            <td
                              className={`text-end ${row.amount_credit ? "text-success" : ""
                                }`}
                              style={cellStyle}
                            >
                              {row.amount_credit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.silver_debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.silver_debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.amount_debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.amount_debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.balance || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.silver_balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.silver_balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.silver_balance || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.amount_balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.amount_balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.amount_balance || "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {ledgerRows.length > 0 && (
                      <tfoot className="fw-bold" style={{ backgroundColor: "#f0e6d2" }}>
                        <tr>
                          <td></td>
                          <td className="text-center">TOTAL</td>
                          <td className="text-end text-success">
                            {totals.creditGold ? totals.creditGold.toFixed(3) : "-"}
                          </td>
                          <td className="text-end text-success">
                            {totals.creditSilver ? totals.creditSilver.toFixed(3) : "-"}
                          </td>
                          <td className="text-end text-success">
                            {totals.creditAmount ? totals.creditAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                          </td>
                          <td className="text-end" style={{ color: "#dc3545" }}>
                            {totals.debitGold ? totals.debitGold.toFixed(3) : "-"}
                          </td>
                          <td className="text-end" style={{ color: "#dc3545" }}>
                            {totals.debitSilver ? totals.debitSilver.toFixed(3) : "-"}
                          </td>
                          <td className="text-end" style={{ color: "#dc3545" }}>
                            {totals.debitAmount ? totals.debitAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                          </td>
                          <td className="text-end" style={{ color: "#000" }}>
                            {totals.closingGold ? totals.closingGold.toFixed(3) : "0.000"}
                          </td>
                          <td className="text-end" style={{ color: "#000" }}>
                            {totals.closingSilver ? totals.closingSilver.toFixed(3) : "0.000"}
                          </td>
                          <td className="text-end" style={{ color: "#000" }}>
                            {totals.closingAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {entireReport && totalCount > 0 && (
                  <Pagination
                    totalItems={totalCount}
                    hasMore={hasMorePages}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={(page) => {
                      if (ignoreNextPageFetchRef.current) {
                        ignoreNextPageFetchRef.current = false;
                        return;
                      }
                      handleSubmit(undefined, { page, limit: itemsPerPage });
                    }}
                    onItemsPerPageChange={(limit) => {
                      ignoreNextPageFetchRef.current = true;
                      setItemsPerPage(limit);
                      setCurrentPage(1);
                      handleSubmit(undefined, {
                        page: 1,
                        limit,
                        bypassCache: true,
                      });
                    }}
                    itemsPerPageOptions={[10, 20, 30, 50, 100]}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Print Loading Modal */}
      <Modal
        show={printLoading}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="mb-2">Preparing Karigar Ledger PDF</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
            This may take a few minutes. Thank you for your patience and understanding.
          </p>
        </Modal.Body>
      </Modal>

      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => {
          setShowPDFModal(false);
          if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
          }
          setPdfBlobUrl(null);
        }}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Supplier/Karigar Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", padding: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title="Karigar Ledger PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default KarigarLedger;
